import { FunnelChart } from '@wundo/ui';
import type { MetricDataPoint } from '@/lib/dashboard.ts';
import { ChartCard } from './ChartCard.tsx';
import { formatPct, sumPoints } from './funnelMath.ts';

export interface RoleplayFunnelCardProps {
  received: MetricDataPoint[];
  clicked: MetricDataPoint[];
  voiceStarted: MetricDataPoint[];
  chatStarted: MetricDataPoint[];
  voiceFinished: MetricDataPoint[];
  chatFinished: MetricDataPoint[];
}

/**
 * How far a roleplay suggestion gets: offered, opened, started, finished.
 *
 * Voice and chat are only separable from Start onwards — the mode is chosen
 * there — so they ride along as the hints under those two steps rather than as
 * two funnels that share their first half.
 */
export function RoleplayFunnelCard(props: RoleplayFunnelCardProps) {
  const received = sumPoints(props.received);
  const clicked = sumPoints(props.clicked);
  const voiceStarted = sumPoints(props.voiceStarted);
  const chatStarted = sumPoints(props.chatStarted);
  const voiceFinished = sumPoints(props.voiceFinished);
  const chatFinished = sumPoints(props.chatFinished);

  return (
    <ChartCard
      title='Roleplay funnel'
      description={
        'Received → Clicked → Started → Finished. Mode is chosen at Start, so Received and ' +
        'Clicked aren’t split.'
      }
      aside={
        <p className='text-xs text-ink-muted'>
          {`End-to-end: voice ${formatPct(voiceFinished, received)} · chat ${formatPct(
            chatFinished,
            received,
          )}`}
        </p>
      }
    >
      <FunnelChart
        ariaLabel='Roleplay funnel'
        steps={[
          { label: 'Received', value: received },
          { label: 'Clicked', value: clicked },
          {
            label: 'Started',
            value: voiceStarted + chatStarted,
            hint: `Voice ${voiceStarted} · chat ${chatStarted}`,
          },
          {
            label: 'Finished',
            value: voiceFinished + chatFinished,
            hint: `Voice ${voiceFinished} (${formatPct(voiceFinished, voiceStarted)}) · chat ${
              chatFinished
            } (${formatPct(chatFinished, chatStarted)})`,
          },
        ]}
      />
    </ChartCard>
  );
}
